/**
 * Route table composition — platform/org/branch route modules are loaded via dynamic import()
 * so their dependency graphs stay out of the main chunk. Do not add static imports of large
 * route files or feature services here. See docs/architecture/frontend-bundling.md.
 */
import { useEffect, useMemo, useState } from 'react'
import type { RouteObject } from 'react-router-dom'
import { useRoutes } from 'react-router-dom'
import { AppLoader } from '@/components/loaders/AppLoader'
import { publicNotFoundRoute, publicRoutesWithoutNotFound } from './public/public.routes'
import { authRoutes } from './auth/auth.routes'

type DeferredState =
  | { status: 'loading' }
  | { status: 'ready'; routes: RouteObject[] }
  | { status: 'error' }

function DeferredRoutesFallback() {
  return (
    <AppLoader
      action="load"
      title="Loading your workspace..."
      description="Setting up features and permissions"
      entityName="workspace"
    />
  )
}

function DeferredRoutesError() {
  return (
    <div className="flex min-h-[40vh] w-full flex-col items-center justify-center gap-3 px-4 text-center text-sm text-muted-foreground">
      <p>Could not load application routes. Check your connection and refresh the page.</p>
      <button
        type="button"
        className="rounded-md border border-input bg-background px-3 py-1.5 text-foreground hover:bg-accent"
        onClick={() => window.location.reload()}
      >
        Reload
      </button>
    </div>
  )
}

export default function AppRoutes() {
  const [deferred, setDeferred] = useState<DeferredState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    setDeferred({ status: 'loading' })

    Promise.all([
      import('./platform/platform.routes'),
      import('./organization/organization.routes'),
      import('./branch/branch.routes'),
    ])
      .then(([platform, organization, branch]) => {
        if (cancelled) return
        setDeferred({
          status: 'ready',
          routes: [
            ...platform.platformRoutes,
            ...organization.organizationRoutes,
            ...branch.branchRoutes,
          ],
        })
      })
      .catch((err) => {
        console.error('Failed to load deferred routes', err)
        if (!cancelled) setDeferred({ status: 'error' })
      })

    return () => {
      cancelled = true
    }
  }, [])

  const routes = useMemo(() => {
    const base = [...authRoutes, ...publicRoutesWithoutNotFound] as RouteObject[]

    if (deferred.status === 'loading') {
      return [...base, { path: '*', element: <DeferredRoutesFallback /> }]
    }
    if (deferred.status === 'error') {
      return [...base, { path: '*', element: <DeferredRoutesError /> }]
    }
    return [...base, ...deferred.routes, publicNotFoundRoute]
  }, [deferred])

  return useRoutes(routes)
}
